import { useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { Ban, PowerOff } from 'lucide-react';
import type { CliActionResult } from '@simpleacs/shared';
import { api } from '../lib/api';
import { useTasks } from '../lib/tasks';
import { formatRelative, formatTimestamp } from '../lib/format';
import { Button, Field, Input, Pill } from './ui';

/** Matches `setDisabledSchema` on the API, so the cap is met at the keyboard. */
const REASON_MAX = 300;

/** What the API sends back, and the two fields every caller here reads. */
interface DisabledState {
  disabledAt: number | null;
  disabledReason: string | null;
}

/** A chassis or one subscriber's ONU. The copy differs; nothing else does. */
type Kind = 'olt' | 'onu';

/**
 * What a disable actually stops, listed rather than summarised.
 *
 * The list is the whole point of the dialog. "Disable" on its own reads like a
 * port shutdown to anybody who has met one, and this is not that — so the
 * consequences are spelled out before the button is pressed, and every line is
 * something SimpleACS stops doing rather than something the network stops
 * doing.
 */
const SUSPENDS: Record<Kind, string[]> = {
  olt: [
    'It is no longer polled or swept. Status, uptime, optics and ONU state stay at the last reading anybody took.',
    'Scheduled backups of it stop.',
    'Bulk runs naming this chassis are refused — at the plan, so the count an operator sees is never a promise that was quietly dropped.',
    'Every single-ONU write on it is refused by name.',
  ],
  onu: [
    'It never matches a bulk filter — absent from the plan rather than skipped during the run.',
    'Every write to it is refused by name.',
  ],
};

/** The sentence that has to survive being skim-read, in both directions. */
const UNAFFECTED: Record<Kind, string> = {
  olt: 'Nothing is sent to the chassis. Its ONUs keep the configuration they have and every subscriber on it stays online — this governs what SimpleACS does, not what the fibre does.',
  onu: 'Nothing is sent to the ONU. It keeps the configuration it has and the subscriber stays online — this governs what SimpleACS does, not what the fibre does.',
};

/*
  Four prefixes rather than the one key the caller is looking at. Disabling a
  chassis changes how its own page, the fleet list and every ONU row underneath
  it must read, and `invalidateQueries` matches by prefix — `['olt']` covers
  `['olt', id, 'onus']` too. RenameDialog next to this already leans on exactly
  that. One list, shared by the Enable button's hook and the Disable dialog's
  tray task, so the two halves of the same switch cannot refresh differently.
*/
const DISABLED_INVALIDATE: QueryKey[] = [['olt'], ['olts'], ['onu'], ['onus']];

/** What the page says once it lands, in either direction. */
function disabledSentence(disabled: boolean): string {
  return disabled
    ? 'Out of service in SimpleACS. Nothing was sent to the chassis and no subscriber lost service.'
    : 'Back in service. SimpleACS acts on it again from now on.';
}

/**
 * The request the Enable button on each page goes through.
 *
 * Kept as a hook because enabling is one click on the page with no dialog to
 * close; the Disable dialog below runs the same POST through the tasks tray
 * and refreshes the same keys, so the cache invalidation cannot drift between
 * the two halves of the same switch — an enable that refreshed less than the
 * disable did would leave the OLT list still drawing a chassis as out of
 * service after it had been put back.
 */
export function useSetDisabled({
  endpoint,
  onDone,
  onError,
}: {
  /** The POST path — `/olts/:id/disabled` or `/olts/:id/onus/:onuId/disabled`. */
  endpoint: string;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { disabled: boolean; reason?: string | null }) =>
      api.post<DisabledState>(endpoint, input),
    onSuccess: async (_result, input) => {
      await Promise.all(
        DISABLED_INVALIDATE.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
      onDone(disabledSentence(input.disabled));
    },
    onError: (err) =>
      onError(err instanceof Error ? err.message : 'Could not change whether this is in service'),
  });
}

/**
 * Take a chassis or an ONU out of service, with the reason and the consequences
 * in front of the operator.
 *
 * This is the confirmation for the destructive direction, and it is a dialog
 * rather than a `confirm()` because there are two things to say and a `confirm`
 * can only hold one: what stops, and — the part that would cost somebody their
 * afternoon if it were missed — that the subscriber's service does not. Putting
 * the reason field in the same step is what makes it get filled in; a separate
 * "add a reason" prompt afterwards is one nobody answers.
 *
 * Only ever opened for disabling. Enabling is not destructive, clears the
 * reason server-side, and is a single click on the page.
 *
 * Like every write dialog, it CLOSES on Disable and the POST runs in the
 * tasks tray; the toast names the OLT or ONU because the dialog is gone by
 * the time the answer arrives.
 */
/**
 * The chassis half of an ONU's Disable (operator, 2026-09-09: one switch, not
 * two). When the OLT reports the line's admin state, the same dialog also
 * deactivates the line on the chassis — which DOES cut the subscriber — so
 * the copy flips from reassurance to warning, and the chassis's own dry-run
 * warnings (a slot whose occupant changed, say) are read before the button.
 */
export interface ChassisLineHalf {
  oltName: string;
  preview: () => Promise<CliActionResult>;
  run: () => Promise<CliActionResult>;
}

export function DisableDialog({
  kind,
  name,
  endpoint,
  href,
  chassis,
  onClose,
}: {
  kind: Kind;
  /** What the operator called it — an OLT name, or an ONU serial. */
  name: string;
  endpoint: string;
  /** Where the tray row and toast link — the page this was opened from. */
  href?: string;
  /** Present when the line is also to be deactivated on the chassis. */
  chassis?: ChassisLineHalf;
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [reason, setReason] = useState('');

  const trimmed = reason.trim();

  const plan = useQuery({
    queryKey: ['disable-line-preview', endpoint],
    queryFn: () => chassis!.preview(),
    enabled: Boolean(chassis),
    staleTime: 0,
  });

  const startDisable = () => {
    const payload = { disabled: true, reason: trimmed || null };
    const line = chassis;
    onClose();
    tasks.start<DisabledState>({
      label: line ? `Disable ${name}` : `Take ${name} out of service`,
      ...(href ? { href } : {}),
      run: async () => {
        const state = await api.post<DisabledState>(endpoint, payload);
        if (line) await line.run();
        return state;
      },
      done: () =>
        line
          ? `${name} is out of service in SimpleACS and its line is deactivated on ${line.oltName} — the subscriber is offline until it is enabled again.`
          : `${name} is out of service in SimpleACS. Nothing was sent to the chassis and no subscriber lost service.`,
      invalidate: [...DISABLED_INVALIDATE, ['onu-admin-state']],
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">
            {chassis ? `Disable ${name}?` : `Take ${name} out of service?`}
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            {chassis
              ? `Out of service in SimpleACS, and the line deactivated on ${chassis.oltName}. Not a delete: the rows, the history and the chassis configuration are kept, and both come back when it is enabled again.`
              : 'A decision about what SimpleACS does with it \u2014 not a reading, and not a delete. Its rows and its history are kept, and it comes straight back when it is enabled again.'}
          </p>
        </header>

        <div className="space-y-4 p-5">
          <div>
            <span className="label">While it is disabled</span>
            <ul className="mt-1 space-y-1.5">
              {SUSPENDS[kind].map((line) => (
                <li key={line} className="flex items-start gap-2 text-sm text-ink">
                  <Ban className="mt-0.5 size-3.5 shrink-0 text-warn" />
                  <span>{line}</span>
                </li>
              ))}
              {chassis && (
                <li className="flex items-start gap-2 text-sm text-ink">
                  <PowerOff className="mt-0.5 size-3.5 shrink-0 text-danger" />
                  <span>
                    {chassis.oltName} deactivates the line and stops serving this ONU until it is
                    enabled again.
                  </span>
                </li>
              )}
            </ul>
          </div>

          {/* The reassurance gets its own box rather than a line in the list
              above, because it is the opposite of everything in that list and
              an operator reading down a column of "stops" will carry the last
              item's meaning into it. With the chassis half it is the opposite
              sentence, and red. */}
          {chassis ? (
            <div className="space-y-2">
              <p className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
                <span className="font-medium">The subscriber loses internet service</span> while
                the line is deactivated. Enable puts it back; the ONU re-ranges in about a minute.
              </p>
              {plan.isLoading ? (
                <p className="text-xs text-muted">Checking the line against {chassis.oltName}…</p>
              ) : plan.error ? (
                <p className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
                  {chassis.oltName} could not check the line first:{' '}
                  {plan.error instanceof Error ? plan.error.message : 'no answer'}. Disabling still
                  runs both halves; the chassis answer arrives in the tray.
                </p>
              ) : (
                (plan.data?.warnings ?? []).map((w) => (
                  <p
                    key={w}
                    className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn"
                  >
                    {w}
                  </p>
                ))
              )}
            </div>
          ) : (
            <p className="rounded-lg border border-success/30 bg-success-soft px-3 py-2 text-sm text-success">
              <span className="font-medium">
                {kind === 'olt'
                  ? "The subscribers' internet service is unaffected."
                  : "The subscriber's internet service is unaffected."}
              </span>{' '}
              {UNAFFECTED[kind]}
            </p>
          )}

          <Field
            label="Reason"
            hint={`Optional, and for the next person who wonders why this stopped reporting. Up to ${REASON_MAX} characters, and enabling clears it.`}
          >
            <Input
              value={reason}
              autoFocus
              maxLength={REASON_MAX}
              placeholder={
                kind === 'olt'
                  ? 'Decommissioned, customer left, lab chassis…'
                  : 'Subscriber in dispute, unit on the bench…'
              }
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') startDisable();
                if (e.key === 'Escape') onClose();
              }}
            />
          </Field>
        </div>

        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" icon={<Ban className="size-4" />} onClick={startDisable}>
            Disable
          </Button>
        </footer>
      </div>
    </div>
  );
}

/**
 * That this is out of service, at the top of its own page.
 *
 * Amber, and above everything, because the reader is here to find out why a
 * chassis stopped reporting or why a write was refused, and the answer is not
 * anywhere in the readings below — those are all frozen at whatever the last
 * poll found. The second half of the copy is the load-bearing half: an operator
 * who reads "disabled" and assumes the subscriber is cut off will go and
 * "fix" something that is not broken.
 */
export function DisabledBanner({
  kind,
  name,
  disabledAt,
  disabledReason,
}: {
  kind: Kind;
  name: string;
  disabledAt: number | null | undefined;
  disabledReason: string | null | undefined;
}) {
  if (!disabledAt) return null;
  return (
    <div className="mb-4 flex items-start gap-2 rounded-lg border border-warn/30 bg-warn-soft px-4 py-3 text-sm text-warn">
      <Ban className="mt-0.5 size-4 shrink-0" />
      <span>
        <span className="font-medium">Out of service in SimpleACS.</span> {name} was disabled{' '}
        {/* "3d ago" is what the sentence needs to read; the exact stamp is what
            gets correlated against the ticket that caused it, so it is on the
            hover rather than dropped. */}
        <span title={formatTimestamp(disabledAt)}>{formatRelative(disabledAt)}</span>
        {disabledReason ? ` — ${disabledReason}` : ''}.{' '}
        {kind === 'olt'
          ? 'It is not polled, not swept and not backed up, bulk runs naming it are refused, and every single-ONU write on it is refused.'
          : 'It never matches a bulk filter, and every write to it is refused by name.'}{' '}
        {UNAFFECTED[kind]}
      </span>
    </div>
  );
}

/**
 * The same fact on a row or beside a heading, where there is no room to explain
 * it. Marked rather than columned, like the `other ACS` pill next to it on the
 * ONU list: almost nothing is disabled, and a column would spend width saying
 * so thousands of times.
 *
 * Amber, not the red the Disable BUTTON wears. On a control red means "this is
 * the destructive direction" and is read as an instruction; in a status column
 * red already means `los` and `down`, and a subscriber marked in the same red
 * as a fibre break is a call-out somebody makes for nothing. `absent` and
 * `other ACS` sit in this same amber register for the same reason: the row is
 * not what it looks like, and nothing is broken.
 */
export function DisabledPill({
  kind,
  disabledReason,
  className,
}: {
  kind: Kind;
  disabledReason: string | null | undefined;
  className?: string;
}) {
  return (
    <span
      className={className ?? 'align-middle'}
      title={`Taken out of service in SimpleACS${
        disabledReason ? ` — ${disabledReason}` : ''
      }. ${
        kind === 'olt'
          ? 'It is not polled or swept, so the figures on this row are the last ones taken; bulk runs and writes on it are refused.'
          : 'It never matches a bulk filter and every write to it is refused.'
      } Subscriber service is unaffected.`}
    >
      <Pill tone="warn">
        <Ban className="size-3" />
        disabled
      </Pill>
    </span>
  );
}
